<script setup>
import { ref, computed } from "vue";
import vueFilePond from "vue-filepond";
import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";

const FilePond = vueFilePond(
  FilePondPluginImagePreview,
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImageResize,
  FilePondPluginImageTransform
);

const props = defineProps({
  userType: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["files-updated"]);

const pond = ref(null);

const filePondOptions = ref({
  acceptedFileTypes: ["image/png", "image/jpeg", "image/webp", "image/avif"],
  maxFiles: 100,
  maxFileSize: "3MB",
  imageResizeTargetWidth: 800,
  imageResizeTargetHeight: 800,
  imageResizeMode: "cover",
  imageTransformOutputQuality: 0.7,
});

const labelIdle = computed(() => {
  return props.userType === "Student"
    ? 'Drag & Drop student images or <span class="filepond--label-action">Browse</span>'
    : props.userType === "Employee"
    ? 'Drag & Drop employee images or <span class="filepond--label-action">Browse</span>'
    : 'Drag & Drop images or <span class="filepond--label-action">Browse</span>';
});

const handleFilePondInit = () => {
  console.log("FilePond initialized");
};

const handleFilesUpdate = (files) => {
  emit("files-updated", files);
};

const getFiles = () => {
  return pond.value ? pond.value.getFiles() : [];
};

const clearFiles = () => {
  if (pond.value) {
    pond.value.removeFiles();
  }
};

const dialog = ref(false); // already defined

// const openDialog = () => {
//    if (pond.value) {
//     pond.value.browse() // ✅ triggers file picker
//   }
//   dialog.value = true
// }

const openFileDialog = () => {
  if (pond.value) {
    pond.value.browse();
  }
};

defineExpose({
  openFileDialog,
  getFiles,
  clearFiles,
});
</script>

<template>
  <div class="filepond-dropzone-card">
    <div class="dropzone-header">
      <v-icon color="primary" size="28">mdi-cloud-upload</v-icon>
      <h3 class="upload-title">Drag & Drop your images here</h3>
      <p class="upload-subtitle">or click to browse from your device 📁</p>
    </div>

    <div class="filepond-inner">
      <file-pond
        ref="pond"
        name="images"
        class="custom-filepond"
        :allow-multiple="true"
        :accepted-file-types="filePondOptions.acceptedFileTypes"
        :max-files="filePondOptions.maxFiles"
        :max-file-size="filePondOptions.maxFileSize"
        :label-idle="labelIdle"
        :allow-image-preview="true"
        :image-preview-height="100"
        :style-panel-aspect-ratio="0.2"
        :credits="false"
        :image-resize-target-width="filePondOptions.imageResizeTargetWidth"
        :image-resize-target-height="filePondOptions.imageResizeTargetHeight"
        :image-resize-mode="filePondOptions.imageResizeMode"
        :image-transform-output-quality="
          filePondOptions.imageTransformOutputQuality
        "
        :disabled="isLoading"
        @init="handleFilePondInit"
        @updatefiles="handleFilesUpdate"
      />
    </div>

    <p class="filepond-hint">
      <v-icon small color="info">mdi-information</v-icon>
      File names should match
      <strong>{{
        userType === "Student"
          ? "student roll numbers"
          : "employee contact numbers"
      }}</strong>
    </p>
  </div>
</template>
<style scoped>
.filepond-dropzone-card {
  border: 2px dashed #1976d2;
  border-radius: 8px;
  padding: 24px;
  transition: all 0.3s;
}

.filepond-dropzone-card:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.filepond--item {
  width: calc(50% - 0.5em);
}

@media (min-width: 768px) {
  .filepond--item {
    width: calc(33% - 0.5em);
  }
}
</style>
